import React, { useState } from 'react';

import { Diamond } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import Grid from '@mui/material/Grid2';

import {
  CreatureLimitation,
  Encounter,
  Environment,
  ItemLimitation,
  TrueCreatureInstance,
} from "@dndModels";

import API from "@utilities/smart_api";
import { EncounterDifficulty } from "@dndUtilities/encounter_generator";
import {
  LOOT_ROLLS_BY_DIFFICULTY,
  LootCandidate,
  LootLookups,
  RolledLoot,
  fittingEntries,
  moneyString,
  rerollLoot,
  rollLoot,
  totalMoney,
} from "@dndUtilities/loot_roller";
import {
  EncounterLootSource,
  buildLootContext,
  encounterCreatures,
  grantLootToCampaign,
  loadLootSources,
  makeLootLookups,
} from "@dndUtilities/encounter_loot";

import ModalOrDrawer from "@components/display/ModalOrDrawer";
import ButtonBox from '@components/input/buttons/ButtonBox';
import LoadingBox from "@components/input/LoadingBox";
import StringBox from "@components/input/strings/StringBox";

const api = API.getInstance();

interface Props {
  encounter: Encounter;
  party_color: string;
  // What the difficulty bar shows for the party. Super Easy rolls like Easy.
  difficulty?: EncounterDifficulty | "Super Easy";
  onGranted: () => void;
}

type Candidate = LootCandidate<CreatureLimitation, ItemLimitation>;

export default function EncounterLootButton({
  encounter,
  party_color,
  difficulty: bar_difficulty,
  onGranted,
}: Props) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [granting, setGranting] = useState(false);
  const [sources, setSources] = useState<EncounterLootSource[]>([]);
  const [environments, setEnvironments] = useState<Environment[]>([]);
  const [creatures, setCreatures] = useState<TrueCreatureInstance[]>([]);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [lookups, setLookups] = useState<LootLookups<CreatureLimitation, ItemLimitation> | null>(null);
  const [rolls, setRolls] = useState(1);
  const [results, setResults] = useState<RolledLoot[]>([]);
  const [granted_message, setGrantedMessage] = useState("");

  const campaign = encounter.campaign;
  const has_weighted = candidates.some(o => !o.entry.guaranteed);
  const difficulty: EncounterDifficulty = !bar_difficulty || bar_difficulty === "Super Easy" ? "Easy" : bar_difficulty;

  const openModal = async () => {
    setOpen(true);
    setGrantedMessage("");
    if (!campaign) {
      return;
    }
    setLoading(true);
    const [{ sources: the_sources, sub_tables }, env_res] = await Promise.all([
      loadLootSources(campaign),
      api.getObjects({ data_type: "environment" }),
    ]);
    const the_lookups = await makeLootLookups(campaign, sub_tables);
    const the_environments = env_res as Environment[];
    const the_creatures = encounterCreatures(encounter, party_color);
    const ctx = buildLootContext(encounter, the_creatures, difficulty, the_environments.map(o => o._id));
    setSources(the_sources);
    setEnvironments(the_environments);
    setCreatures(the_creatures);
    setCandidates(fittingEntries(the_sources, ctx));
    setLookups(the_lookups);
    setRolls(LOOT_ROLLS_BY_DIFFICULTY[difficulty]);
    setLoading(false);
  }

  const roll = () => {
    if (lookups) {
      setResults(rollLoot({ candidates, rolls, lookups }));
      setGrantedMessage("");
    }
  }

  const reroll = (result: RolledLoot) => {
    if (lookups) {
      const new_result = rerollLoot({ result, candidates, lookups });
      if (new_result) {
        setResults(results.map(o => o.key === result.key ? new_result : o));
      }
    }
  }

  const grant = async () => {
    if (!campaign) {
      return;
    }
    setGranting(true);
    try {
      await grantLootToCampaign(campaign, results);
      setGrantedMessage(`Added ${results.length} result(s) to the campaign inventory.`);
      setResults([]);
      onGranted();
    } finally {
      setGranting(false);
    }
  }

  const renderSummary = () => {
    const env_names = environments.filter(o => encounter.environment_ids.includes(o._id)).map(o => o.name);
    const max_cr = Math.max(0, ...creatures.map(o => o.challenge_rating));
    return (
      <Grid size={12} className="small_label gray">
        <div>Difficulty: { difficulty }{ bar_difficulty === "Super Easy" ? " (Super Easy rolls like Easy)" : "" }</div>
        <div>Environments: { env_names.length > 0 ? env_names.join(", ") : "None (everything fits)" }</div>
        <div>Creatures Faced: { creatures.length }, Highest CR: { max_cr }</div>
        <div>Loot Tables: { sources.map(o => o.table_name).join(", ") }</div>
        <div>
          { candidates.length } of { sources.reduce((total, o) => total + o.entries.length, 0) } entries fit this encounter
          ({ candidates.filter(o => o.entry.guaranteed).length } guaranteed).
        </div>
      </Grid>
    );
  }

  const renderResult = (result: RolledLoot) => {
    const money = moneyString(result.money);
    return (
      <Grid key={result.key} size={12} className="padded bordered">
        <Grid container spacing={0} direction="row">
          <Grid size={8}>
            <div className="header">{ result.entry_name === "" ? "(Unnamed Entry)" : result.entry_name }</div>
            <div className="small_label gray">{ result.table_name }{ result.guaranteed ? ", Guaranteed" : "" }</div>
          </Grid>
          <Grid size={2}>
            <ButtonBox name="Reroll" disabled={granting} onClick={() => reroll(result)} />
          </Grid>
          <Grid size={2}>
            <ButtonBox name="Remove" disabled={granting} onClick={() => setResults(results.filter(o => o.key !== result.key))} />
          </Grid>
          { result.items.map(item => (
            <Grid key={item.id} size={12}>
              { item.count } × { item.name }
            </Grid>
          ))}
          { money !== "" &&
            <Grid size={12}>{ money }</Grid>
          }
          { result.sub_rolls.length > 0 &&
            <Grid size={12} className="small_label gray">
              Rolled: { result.sub_rolls.join("; ") }
            </Grid>
          }
          { result.problems.map((problem, index) => (
            <Grid key={index} size={12} className="small_label" style={{ color: "orange" }}>
              { problem }
            </Grid>
          ))}
          { result.items.length === 0 && money === "" && result.problems.length === 0 &&
            <Grid size={12} className="small_label gray">Nothing</Grid>
          }
        </Grid>
      </Grid>
    );
  }

  const renderContent = () => {
    if (!campaign) {
      return <>Loot can only be rolled for encounters in a campaign.</>;
    } else if (loading || granting) {
      return <LoadingBox message={granting ? "Adding Loot to the Campaign" : "Loading Loot Tables"} />;
    } else if (sources.length === 0) {
      return (
        <>
          This campaign doesn&apos;t have any loot tables yet.
          Choose them in the campaign&apos;s Character Options, under Special Rules.
        </>
      );
    }
    const total = moneyString(totalMoney(results));
    const item_count = results.reduce((sum, o) => sum + o.items.reduce((sum2, o2) => sum2 + o2.count, 0), 0);
    return (
      <Grid container spacing={1} direction="row">
        <Grid size={12} className="header centered">
          Encounter Loot
        </Grid>
        { encounter.loot_granted &&
          <Grid size={12}>
            Loot has already been granted for this encounter. Rolling again will give more.
          </Grid>
        }
        { renderSummary() }
        <Grid size={4}>
          <StringBox
            name="Rolls (besides guaranteed entries)"
            type="number"
            value={rolls}
            disabled={!has_weighted}
            onBlur={(changed: string) => setRolls(Math.max(+changed, 0))}
          />
        </Grid>
        <Grid size={8}>
          <ButtonBox
            name={results.length > 0 ? "Reroll All" : "Roll Loot"}
            disabled={candidates.length === 0}
            onClick={roll}
          />
        </Grid>
        { candidates.length === 0 &&
          <Grid size={12}>
            No entries in the campaign&apos;s loot tables fit this encounter.
          </Grid>
        }
        { results.map(renderResult) }
        { results.length > 0 &&
          <>
            <Grid size={12} className="header">
              Total: { item_count } item(s){ total !== "" ? `, ${total}` : "" }
            </Grid>
            <Grid size={12}>
              <ButtonBox
                name="Add to Campaign Inventory"
                disabled={granting}
                onClick={grant}
              />
            </Grid>
          </>
        }
        { granted_message !== "" &&
          <Grid size={12}>{ granted_message }</Grid>
        }
      </Grid>
    );
  }

  return (
    <>
      <Tooltip title="Roll Loot">
        <Diamond className="clickable" onClick={openModal} />
      </Tooltip>
      { open &&
        <ModalOrDrawer
          open={open}
          width={800}
          onClose={() => { setOpen(false); }}>
          { renderContent() }
        </ModalOrDrawer>
      }
    </>
  );
}
